/**
 * Attribute Editor Panel
 *
 * Form-based editor for ATTRIBUTE artifacts
 */

import React, { useState, useEffect } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { Settings, Save, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '@/lib/utils';

type AttributeDataType = 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'DATE' | 'JSON';
type AttributeScope = 'global' | 'local' | 'session';

interface AttributeMetadata {
  dataType: AttributeDataType;
  scope: AttributeScope;
  defaultValue?: string | number | boolean;
}

export const AttributeEditorPanel: React.FC = () => {
  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const updateArtifact = useProjectStore((state) => state.updateArtifact);

  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState<AttributeDataType>('TEXT');
  const [scope, setScope] = useState<AttributeScope>('global');
  const [defaultValue, setDefaultValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Parse lumaJson metadata on artifact change
  useEffect(() => {
    if (!artifact) return;

    setName(artifact.name);
    setDescription(artifact.description || '');

    if (artifact.lumaJson) {
      let metadata: AttributeMetadata | null = null;
      if (typeof artifact.lumaJson === 'string') {
        try {
          metadata = JSON.parse(artifact.lumaJson) as AttributeMetadata;
        } catch (error) {
          console.warn('[AttributeEditor] Failed to parse lumaJson metadata', error);
        }
      } else {
        metadata = artifact.lumaJson as AttributeMetadata;
      }

      if (metadata) {
        setDataType(metadata.dataType || 'TEXT');
        const normalizedScope = (metadata.scope || 'global').toLowerCase() as AttributeScope;
        setScope(normalizedScope === 'session' ? 'global' : normalizedScope);
        setDefaultValue(metadata.defaultValue?.toString() || '');
      } else {
        setDataType('TEXT');
        setScope('global');
        setDefaultValue('');
      }
    } else {
      // Set defaults
      setDataType('TEXT');
      setScope('global');
      setDefaultValue('');
    }

    setIsDirty(false);
  }, [artifact?.id, artifact?.name, artifact?.description, artifact?.lumaJson, artifact?.updatedAt]);

  // Mark form as dirty when fields change
  const handleFieldChange = (setter: React.Dispatch<React.SetStateAction<any>>, value: any) => {
    setter(value);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!activeProjectId || !selectedArtifactId || !artifact) return;

    setIsSaving(true);
    try {
      // Build lumaJson metadata
      const metadata: AttributeMetadata = {
        dataType,
        scope,
        defaultValue: defaultValue ? parseDefaultValue(defaultValue, dataType) : undefined
      };

      // Update artifact
      await updateArtifact(activeProjectId, selectedArtifactId, {
        name,
        description,
        lumaJson: metadata
      });

      setIsDirty(false);
    } catch (error: any) {
      console.error('Failed to save attribute:', error);
      alert(`Failed to save: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Parse default value based on data type
  const parseDefaultValue = (value: string, type: AttributeDataType): string | number | boolean => {
    if (!value) return '';

    switch (type) {
      case 'NUMBER':
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
      case 'BOOLEAN':
        return value.toLowerCase() === 'true' || value === '1';
      case 'DATE':
      case 'TEXT':
      case 'JSON':
      default:
        return value;
    }
  };

  if (!artifact || artifact.type !== 'ATTRIBUTE') {
    return (
      <div className="panel-container">
        <div className="panel-content flex items-center justify-center h-full text-muted-foreground">
          <p className="text-sm">No attribute artifact selected.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="panel-container">
      <div className="panel-header">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4" />
          <h3 className="text-sm font-semibold">Attribute Editor</h3>
        </div>
      </div>

      <div className="panel-content p-6 overflow-auto">
        <div className="max-w-2xl">
          <div className="mb-6">
            <h4 className="text-lg font-semibold mb-1">Edit Attribute</h4>
            <p className="text-sm text-muted-foreground">
              Define metadata for this context attribute
            </p>
          </div>

          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="attr-name" className="block text-sm font-medium mb-2">
                Name *
              </label>
              <input
                id="attr-name"
                type="text"
                value={name}
                onChange={(e) => handleFieldChange(setName, e.target.value)}
                placeholder="e.g., Ticket Category"
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Identifier (read-only) */}
            <div>
              <label htmlFor="attr-identifier" className="block text-sm font-medium mb-2">
                Identifier (Auto-generated)
              </label>
              <input
                id="attr-identifier"
                type="text"
                value={artifact.fullIdentifier}
                disabled
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-muted text-muted-foreground cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Base: {artifact.identifier} · Suffix: {project?.suffix}
              </p>
            </div>

            {/* Data Type */}
            <div>
              <label htmlFor="attr-datatype" className="block text-sm font-medium mb-2">
                Data Type *
              </label>
              <select
                id="attr-datatype"
                value={dataType}
                onChange={(e) => handleFieldChange(setDataType, e.target.value as AttributeDataType)}
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="TEXT">TEXT</option>
                <option value="NUMBER">NUMBER (FLOAT in Luma)</option>
                <option value="BOOLEAN">BOOLEAN</option>
                <option value="DATE">DATE</option>
                <option value="JSON">JSON</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                Note: NUMBER maps to FLOAT in Luma
              </p>
            </div>

            {/* Scope */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Scope *
              </label>
              <div className="flex gap-2">
                {(['global', 'local'] as AttributeScope[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleFieldChange(setScope, option)}
                    className={cn(
                      'flex-1 px-3 py-2 rounded-md border text-sm transition-colors',
                      scope === option
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:bg-accent'
                    )}
                  >
                    {option === 'global' ? 'Global' : 'Local'}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Global: Persists across the conversation · Local: Scoped to the current skill execution
              </p>
            </div>

            {/* Default Value */}
            <div>
              <label htmlFor="attr-default" className="block text-sm font-medium mb-2">
                Default Value (Optional)
              </label>
              <input
                id="attr-default"
                type="text"
                value={defaultValue}
                onChange={(e) => handleFieldChange(setDefaultValue, e.target.value)}
                placeholder={dataType === 'BOOLEAN' ? 'true or false' : dataType === 'NUMBER' ? 'e.g., 0' : 'e.g., Not specified'}
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="attr-description" className="block text-sm font-medium mb-2">
                Description *
              </label>
              <textarea
                id="attr-description"
                value={description}
                onChange={(e) => handleFieldChange(setDescription, e.target.value)}
                placeholder="Describe the purpose and usage of this attribute..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>

            {/* Dirty indicator */}
            {isDirty && (
              <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
                <AlertCircle className="w-4 h-4" />
                <span>Unsaved changes</span>
              </div>
            )}

            {/* Save button */}
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSave} disabled={isSaving || !isDirty}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>

            {/* Metadata Display */}
            <div className="pt-6 border-t border-border">
              <h5 className="text-sm font-semibold mb-2">Artifact Metadata</h5>
              <div className="text-xs text-muted-foreground space-y-1">
                <p><strong>Artifact ID:</strong> {artifact.id}</p>
                <p><strong>Type:</strong> {artifact.type}</p>
                <p><strong>Created:</strong> {new Date(artifact.createdAt).toLocaleString()}</p>
                <p><strong>Updated:</strong> {new Date(artifact.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
